<template>
    <div>
        <div v-if="loading" data-testid="cesion-event-list-loading" class="tw:space-y-4">
            <div
                v-for="n in 3"
                :key="n"
                class="tw:overflow-hidden tw:rounded-[30px] tw:border tw:border-slate-200 tw:bg-white tw:px-4 tw:py-5 tw:shadow-[0_22px_55px_-32px_rgba(15,23,42,0.45)]"
            >
                <!-- Centered matchup + arrow row -->
                <div class="tw:flex tw:min-h-[262px] tw:items-center tw:justify-center tw:gap-9">
                    <!-- Matchup sub-card -->
                    <div class="tw:flex tw:min-h-[250px] tw:w-[300px] tw:items-center tw:justify-center tw:rounded-[28px] tw:border tw:border-slate-200 tw:bg-slate-50 tw:shadow-[0_18px_45px_-34px_rgba(15,23,42,0.45)]">
                        <div class="tw:flex tw:items-center tw:gap-6">
                            <!-- Left team -->
                            <div class="tw:flex tw:flex-col tw:items-center tw:gap-2">
                                <SkeletonItem class="tw:h-20 tw:w-20 tw:rounded-[24px]" />
                                <SkeletonItem class="tw:h-3 tw:w-24" />
                            </div>

                            <!-- VS badge -->
                            <SkeletonItem class="tw:h-10 tw:w-10 tw:rounded-full" />

                            <!-- Right team -->
                            <div class="tw:flex tw:flex-col tw:items-center tw:gap-2">
                                <SkeletonItem class="tw:h-20 tw:w-20 tw:rounded-[24px]" />
                                <SkeletonItem class="tw:h-3 tw:w-24" />
                            </div>
                        </div>
                    </div>

                    <!-- Arrow indicator -->
                    <SkeletonItem class="tw:h-11 tw:w-11 tw:rounded-full" />
                </div>
            </div>
        </div>

        <div v-else-if="error" data-testid="cesion-event-list-error" class="tw:rounded-[28px] tw:border tw:border-red-200 tw:bg-red-50 tw:p-6 tw:text-red-700">
            <div class="tw:flex tw:items-start tw:gap-3">
                <i class="material-icons tw:text-red-500 tw:mt-1">error_outline</i>
                <div class="tw:flex-1">
                    <p class="tw:font-semibold">{{ $t('perfil.cesion_error_cargar_eventos') }}</p>
                    <p class="tw:text-sm tw:mt-1">{{ error }}</p>
                    <Button
                        variant="alt"
                        custom-class="tw:mt-3"
                        @click="$emit('retry')"
                    >
                        {{ $t('perfil.cesion_reintentar') }}
                    </Button>
                </div>
            </div>
        </div>

        <div v-else-if="eventos.length === 0" data-testid="cesion-event-list-empty" class="tw:rounded-[28px] tw:border tw:border-dashed tw:border-slate-300 tw:bg-white/70 tw:p-10 tw:text-center tw:text-slate-500">
            <i class="material-icons tw:text-5xl tw:text-slate-300 tw:block tw:mb-3">event</i>
            <p class="tw:font-semibold">{{ $t('perfil.cesion_no_hay_eventos') }}</p>
            <p class="tw:text-sm tw:mt-1">{{ $t('perfil.cesion_no_eventos_disponibles') }}</p>
        </div>

        <div v-else data-testid="cesion-event-list" class="tw:space-y-4">
            <article
                v-for="evento in eventos"
                :key="evento.id"
                :data-testid="`cesion-evento-card-${evento.id}`"
                class="tw:overflow-hidden tw:rounded-[30px] tw:border tw:border-slate-200 tw:bg-white tw:shadow-[0_22px_55px_-32px_rgba(15,23,42,0.45)] tw:transition-all tw:duration-200 hover:tw:border-slate-300"
            >
                <Button
                    type="button"
                    :aria-expanded="hasMultipleSessions(evento) ? String(isExpanded(evento.id)) : undefined"
                    :aria-controls="hasMultipleSessions(evento) ? getSessionPanelId(evento.id) : undefined"
                    :disabled="isResolving(evento.id)"
                    ignore-base-classes
                    :custom-class="getCardButtonClasses(evento.id).join(' ')"
                    @click="$emit('select-evento', evento)"
                >
                    <div class="tw:flex tw:flex-col tw:lg:flex-row tw:lg:items-center tw:lg:justify-between">
                        <div class="tw:flex-1 tw:space-y-4 tw:text-left">
                            <Matchup
                                :local-team="evento.equipo_local"
                                :away-team="evento.equipo_visitante"
                                :division="evento.division"
                                :show-status="true"
                                :status="getStatusLabel(evento)"
                            />

                            <div v-if="hasMultipleSessions(evento)" class="tw:flex tw:min-w-0 tw:flex-wrap tw:items-center tw:gap-3 tw:text-sm tw:text-slate-500">
                                <span class="tw:inline-flex tw:max-w-full tw:items-center tw:gap-1 tw:rounded-full tw:bg-sky-50 tw:px-3 tw:py-1 tw:font-medium tw:text-sky-700">
                                    <i class="material-icons tw:text-sm">schedule</i>
                                    <span class="tw:min-w-0 tw:break-words">{{ $t('perfil.cesion_sesiones_disponibles', { count: evento.sesiones.length }) }}</span>
                                </span>
                            </div>
                        </div>

                        <div class="tw:flex tw:items-center tw:justify-start tw:lg:w-20 tw:lg:justify-end">
                            <span :class="getIndicatorClasses(evento.id)">
                                <i class="material-icons tw:text-base">{{ getActionIcon(evento) }}</i>
                            </span>
                        </div>
                    </div>
                </Button>

                <div
                    v-if="isExpanded(evento.id)"
                    :id="getSessionPanelId(evento.id)"
                    class="tw:border-t tw:border-slate-200 tw:bg-slate-50/80 tw:px-5 tw:pb-5 tw:pt-4"
                >
                    <div class="tw:flex tw:flex-col tw:gap-3">
                        <div>
                            <p class="tw:text-xs tw:font-black tw:uppercase tw:tracking-[0.26em] tw:text-slate-400">
                                {{ $t('perfil.cesion_seleccionar_sesion') }}
                            </p>
                            <p class="tw:mt-1 tw:text-sm tw:text-slate-500">
                                {{ $t('perfil.cesion_instruccion_sesion') }}
                            </p>
                        </div>
                    </div>

                    <div class="tw:mt-4 tw:flex tw:flex-wrap tw:gap-3">
                        <Button
                            v-for="sesion in expandedSessions"
                            :key="sesion.sesion_id"
                            type="button"
                            :disabled="isResolving(evento.id)"
                            variant="default"
                            custom-class="tw:min-h-[52px] tw:rounded-2xl tw:border tw:border-slate-200 tw:bg-white tw:text-slate-700 hover:tw:bg-slate-100 sm:tw:w-auto tw:disabled:opacity-60"
                            @click.stop="$emit('select-session', sesion.sesion_id)"
                        >
                            {{ getSessionLabel(sesion) }}
                        </Button>
                    </div>

                </div>

                <div
                    v-if="selectionError && selectionErrorEventoId === evento.id && !isResolving(evento.id)"
                    class="tw:mx-5 tw:mb-5 tw:rounded-2xl tw:border tw:border-red-200 tw:bg-red-50 tw:px-4 tw:py-3 tw:text-sm tw:text-red-700"
                >
                    {{ selectionError }}
                </div>
            </article>
        </div>
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import { t } from '@/utils/i18n-helper';
    import { useFilters } from '@/composables/front/formatters/useFilters';
    import Matchup from '@/modules/front/perfil/components/cesion/Matchup.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import { SkeletonItem } from '@/modules/front/ui/skeleton';

    const props = defineProps({
        eventos: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: null
        },
        selectionLoading: {
            type: Boolean,
            default: false
        },
        selectionError: {
            type: String,
            default: null
        },
        selectionErrorEventoId: {
            type: [String, Number],
            default: null
        },
        resolvingEventoId: {
            type: [String, Number],
            default: null
        },
        expandedEventoId: {
            type: [String, Number],
            default: null
        },
        expandedSessions: {
            type: Array,
            default: () => []
        }
    });

    defineEmits(['select-evento', 'select-session', 'retry']);

    const { formatearFecha } = useFilters();

    const formatFecha = fecha => formatearFecha(fecha) ?? '—';
    const expandedSessions = computed(() => Array.isArray(props.expandedSessions) ? props.expandedSessions : []);
    const getStatusLabel = evento => evento.cesion_temporal_activa
        ? t('perfil.cesion_estado_cedido')
        : t('perfil.cesion_estado_disponible');
    const getPrimaryDateLabel = evento => formatFecha(evento.fecha);

    const hasMultipleSessions = evento => Array.isArray(evento?.sesiones) && evento.sesiones.length > 1;
    const isExpanded = eventoId => props.expandedEventoId === eventoId;
    const isResolving = eventoId => props.selectionLoading && props.resolvingEventoId === eventoId;
    const getSessionPanelId = eventoId => `cesion-sesiones-${eventoId}`;
    const getSessionLabel = sesion => formatFecha(sesion.fecha);
    const getActionIcon = evento => hasMultipleSessions(evento) && isExpanded(evento.id)
        ? 'expand_less'
        : hasMultipleSessions(evento)
            ? 'expand_more'
            : isResolving(evento.id)
                ? 'hourglass_top'
                : 'arrow_forward';
    const getCardButtonClasses = eventoId => [
        'tw:group tw:flex tw:w-full tw:flex-col tw:p-5 tw:text-left tw:transition-all tw:duration-200 hover:tw:-translate-y-0.5 hover:tw:shadow-[0_22px_55px_-32px_rgba(15,23,42,0.35)] focus:tw:outline-none focus:tw:ring-2 focus:tw:ring-slate-300 tw:disabled:cursor-wait',
        isExpanded(eventoId) ? 'tw:bg-slate-50/80' : 'tw:bg-white'
    ];
    const getIndicatorClasses = eventoId => [
        'tw:inline-flex tw:h-11 tw:w-11 tw:items-center tw:justify-center tw:rounded-full tw:border tw:transition',
        isExpanded(eventoId)
            ? 'tw:border-sky-200 tw:bg-sky-50 tw:text-sky-800'
            : 'tw:border-slate-200 tw:bg-slate-100 tw:text-slate-600 group-hover:tw:border-slate-300 group-hover:tw:bg-slate-200'
    ];
</script>
